import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Card } from '../schemas/metabase';
import { useCopyCard } from './useCopyCard';
import { useDeleteCollectionItem } from './useDeleteCollectionItem';
import { useUpdateCard } from './useUpdateCard';

type ReportLocationState = {
  prevCard?: Card;
  forceNavigation?: boolean;
};

type UseReportActionsProps = {
  reportId: number;
  collectionId: number;
  browsingCollectionId?: number | null;
  isDraft: boolean;
  resetForm: (values: { name: string; description: string }) => void;
  resetAllPreviews?: () => void;
};

type UseReportActionsReturn = {
  handleRevertLastChange: () => void;
  handleCloseReport: () => void;
  handleDuplicate: () => void;
  handleDelete: () => void;
};

export const useReportActions = ({
  reportId,
  collectionId,
  browsingCollectionId,
  isDraft,
  resetForm,
  resetAllPreviews,
}: UseReportActionsProps): UseReportActionsReturn => {
  const navigateToCollection = browsingCollectionId ?? collectionId;
  const history = useHistory<ReportLocationState>();
  const location = useLocation<ReportLocationState>();
  const { mutate: updateCard } = useUpdateCard();
  const { mutateAsync: copyCard } = useCopyCard();
  const { mutateAsync: deleteCard } = useDeleteCollectionItem();

  const prevCard = location.state?.prevCard;

  const handleRevertLastChange = useCallback(() => {
    resetAllPreviews?.();

    if (prevCard) {
      updateCard({ id: reportId, payload: prevCard });
      resetForm({
        name: prevCard.name,
        description: prevCard.description ?? '',
      });
      history.replace(location.pathname, {
        forceNavigation: true,
      });
    }
  }, [
    prevCard,
    updateCard,
    reportId,
    history,
    location.pathname,
    resetForm,
    resetAllPreviews,
  ]);

  const handleCloseReport = useCallback(() => {
    if (prevCard) {
      updateCard({ id: reportId, payload: prevCard });
    }

    if (isDraft) {
      deleteCard({ id: reportId, type: 'card', hideNotification: true }).then(
        () => {
          history.push(`/reporting/${navigateToCollection}`);
        }
      );
    } else {
      history.push(`/reporting/${navigateToCollection}`);
    }
  }, [
    prevCard,
    history,
    navigateToCollection,
    updateCard,
    reportId,
    isDraft,
    deleteCard,
  ]);

  const handleDuplicate = useCallback(() => {
    copyCard(reportId).then(({ data: { id: duplicateId } }) => {
      if (duplicateId) {
        history.push(`/reporting/report/${duplicateId}`);
      }
    });
  }, [copyCard, reportId, history]);

  const handleDelete = useCallback(() => {
    deleteCard({ id: reportId, type: 'card' }).then(() => {
      history.push(`/reporting/${navigateToCollection}`);
    });
  }, [deleteCard, reportId, history, navigateToCollection]);

  return {
    handleRevertLastChange,
    handleCloseReport,
    handleDuplicate,
    handleDelete,
  };
};
